import type { I18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import type { PriceCheckResponse } from "@/types/api";

export interface MandatoryExtraInfo {
  id: number;
  name: string;
  price: number;
  priceAlt: number;
}

/**
 * Extracts mandatory extras from price components
 */
export function getMandatoryExtrasFromPrice(price: PriceCheckResponse): MandatoryExtraInfo[] {
  if (!price.components) {
    return [];
  }

  const objectsMap: Record<number, MandatoryExtraInfo> = {};

  // go over price components, extract mandatory fees and sum their prices
  for (const component of price.components) {
    if (
      component.extra_fields == null ||
      component.object_type === "person" ||
      component.extra_fields?.is_mandatory === false
    ) {
      continue;
    }
    if (!objectsMap[component.object_id]) {
      objectsMap[component.object_id] = {
        id: component.object_id,
        name: component.name,
        price: component.price ?? 0,
        priceAlt: 0,
      };
    } else {
      objectsMap[component.object_id]!.price += component.price ?? 0;
    }
  }
  // add alternative currency prices to the result
  for (const component of price.currency_alt?.components || []) {
    if (objectsMap[component.object_id]) {
      objectsMap[component.object_id]!.priceAlt += component.price ?? 0;
    }
  }
  return Object.values(objectsMap);
}

/**
 * Gets period label for an extra based on billing type
 * @param billingType - The billing type string
 * @param i18n - The i18n instance for translations
 * @param nights - Optional number of nights to include in the label
 * @returns Translated label string
 */
export function getExtraPeriodLabel(billingType: string, i18n: I18n, nights: number): string {
  if (
    billingType.includes("_night") ||
    billingType.includes("_day") ||
    billingType.includes("once")
  ) {
    return i18n._(msg`for ${nights} ${plural(nights, { one: "night", other: "nights" })}`);
  }

  const labelMap: Record<string, ReturnType<typeof msg>> = {
    once: msg`Once`,
    included: msg`Free`,
    per_consumption: msg`Based on usage`,
    on_site: msg`Paid on site`,
  };

  if (labelMap[billingType]) {
    return i18n._(labelMap[billingType]);
  }

  return billingType;
}

export function isNoPriceBillingType(billingType: string): boolean {
  return ["included", "per_consumption", "on_site"].includes(billingType);
}
